import "./EnhancedRequirementGatheringComponent.scss";
import CustomInputComponent from "@/components/ui/custom-input/CustomInputComponent.tsx";
import { CommonService } from "@/services";
import { Misc } from "@/constants";
import JoyAvatar from "@/components/common-components/joy-avatar/joy-avatar.tsx";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import commonService from "@/services/common.service.ts";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ChatLoaderComponent from "@/components/common-components/chat-loader/ChatLoaderComponent.tsx";
import { MoveDownIcon } from "lucide-react";

import UserAvatarComponent from "@/components/common-components/user-avatar/UserAvatarComponent.tsx";
import { PROJECT_REACT_ROUTE } from "@/constants/RoutesConfig.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import FileUploadComponent from "@/components/ui/file-upload/FileUploadComponent.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDispatch } from "react-redux";
import { setCurrentProjectDetails, setIframeUrl } from "@/store/actions/projectdata.action.ts";
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import RequirementGatheringWelcomeComponent from "@/components/common-components/requirement-gathering-welcome/RequirementGatheringWelcomeComponent.tsx";
import WorkflowInitializationComponent from "@/components/common-components/workflow-initialization/WorkflowInitializationComponent.tsx";
import AgentSelectionComponent, { AgentType } from "@/components/common-components/agent-selection/AgentSelectionComponent.tsx";
import MarkdownTextRenderer from '@/components/common-components/markdown-text-renderer/MarkdownTextRenderer';
import { useIsMobile } from "@/hooks/use-mobile";
import { useRequirementGatheringMode } from "@/hooks/useRequirementGatheringMode";
import { useSocket } from "@/services/hooks/useSocket";
import { getUsersTokensUtilization } from "@/store/actions/meta.action.ts";
import { requirementGatheringDB } from "@/services/requirementGatheringDB";
import type { RequirementGatheringState } from "@/services/requirementGatheringDB";

interface ChatMessage {
    id: number;
    role: 'user' | 'ai' | 'loading';
    content: string;
    file?: any,
    preview: any;
}

type SetupStep = 'workflow-init' | 'agent-selection' | 'initializing' | 'conversation';

// Package installer command (executed when workflow is initialized)
const PACKAGE_INSTALLER_COMMAND = 'npx github:superengineer-ai/superengineer-agents-installer';

// Agent-specific initialization commands (executed after package installation)
const AGENT_INITIALIZATION_COMMANDS = {
    analyst: '/superengineer:se:workflows:workflow-init',
    pm: '/superengineer:se:workflows:create-product-brief'
};

const EnhancedRequirementGatheringComponent = () => {
    const currentUser = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA);
    const formattedUser = currentUser ? JSON.parse(currentUser) : null;
    const userName = formattedUser?.name || "User";
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const greeting = commonService.generateGreeting();
    const chatHistoryRef = useRef<HTMLDivElement>(null);
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
    const [currentFiles, setCurrentFiles] = useState<any>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);

    // Setup flow state
    const [currentStep, setCurrentStep] = useState<SetupStep>('workflow-init');
    const [workflowInitialized, setWorkflowInitialized] = useState<boolean>(false);
    const [selectedAgent, setSelectedAgent] = useState<AgentType | null>(null);
    const [isLoadingFromDB, setIsLoadingFromDB] = useState<boolean>(true);
    const [showScrollIndicator, setShowScrollIndicator] = useState<boolean>(false);

    // Get current prompt and iframe URL from Redux
    const iframeUrl = useSelector((state: IRootReducerState) => state.projectData.iframeUrl);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const previewUrlResolverRef = useRef<{ resolve: (url: string) => void; reject: (error: any) => void } | null>(null);

    const dispatch = useDispatch();

    const isNew = messages?.length === 0;

    // Use the requirement gathering mode hook (for text streaming)
    const { executeRequirementGathering, abort } = useRequirementGatheringMode();

    // Abort on unmount
    useEffect(() => {
        return () => {
            abort();
        };
    }, [abort]);

    // Socket subscription for preview events (for container wake-up)
    const { events: previewEvents, clearEvent: clearPreviewEvent } = useSocket({
        eventTypes: [
            'preview.started',
            'preview.delta',
            'preview.success',
            'preview.error'
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'RequirementGathering-Preview',
        debug: false,
    });

    // Handle preview socket events for container wake-up
    useEffect(() => {
        if (previewEvents['preview.success']) {
            const successEvent = previewEvents['preview.success'];
            const url = successEvent?.event_meta?.preview_url;

            if (url && previewUrlResolverRef.current) {
                dispatch(setIframeUrl(url));
                previewUrlResolverRef.current.resolve(url);
                previewUrlResolverRef.current = null;
            }
            clearPreviewEvent('preview.success');
        }

        if (previewEvents['preview.error']) {
            const errorEvent = previewEvents['preview.error'];

            if (previewUrlResolverRef.current) {
                previewUrlResolverRef.current.reject(new Error(errorEvent?.error || 'Preview generation failed'));
                previewUrlResolverRef.current = null;
            }
            clearPreviewEvent('preview.error');
        }
    }, [previewEvents, clearPreviewEvent]);

    // Function to wait for preview URL from socket
    const waitForPreviewUrl = useCallback((): Promise<string> => {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                if (previewUrlResolverRef.current) {
                    previewUrlResolverRef.current.reject(new Error('Container startup timeout'));
                    previewUrlResolverRef.current = null;
                }
            }, 120000); // 2 minute timeout

            previewUrlResolverRef.current = {
                resolve: (url: string) => {
                    clearTimeout(timeout);
                    resolve(url);
                },
                reject: (error: any) => {
                    clearTimeout(timeout);
                    reject(error);
                }
            };
        });
    }, []);

    const fileRenderer = useCallback((message: any) => {
        return (<>
            {
                message?.file &&
                <div className={'file-message'}>
                    <div
                        className={"p-3 bg-white border border-solid border-gray-300 rounded-xl dark:bg-neutral-800 dark:border-neutral-600 "}>
                        {
                            message?.preview ? (
                                <img
                                    src={message?.preview}
                                    alt="preview"
                                    className="size-4 rounded-lg"
                                />
                            ) : (
                                <span
                                    className="size-4 border border-gray-200 text-gray-500 rounded-lg dark:border-neutral-700 dark:text-neutral-500">
                                    {message?.file?.name?.split(".").pop().toUpperCase()}
                                </span>
                            )
                        }
                    </div>
                    <div className={'file-details'}>
                        <div className={'font-medium'}>
                            {message?.file?.name ? message?.file?.name : "File"}
                        </div>
                        <div className={'file-size'}>
                            {message?.file?.size}
                        </div>
                    </div>
                </div>
            }
            {
                message?.content &&
                <div className={'user-content'}>
                    <MarkdownTextRenderer text={message?.content} />
                </div>
            }

        </>)
    }, [])

    const renderMessages = useCallback((message: ChatMessage) => {
        if (message?.role === 'loading') {
            return <ChatLoaderComponent type={"requirement-step"} />;
        }

        return fileRenderer(message);
    }, [fileRenderer]);

    // Check scroll position to show/hide scroll indicator
    const checkScrollPosition = useCallback(() => {
        if (!chatHistoryRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatHistoryRef.current;
        setShowScrollIndicator(scrollHeight - scrollTop - clientHeight > 5);
    }, []);

    // Scroll event listener
    useEffect(() => {
        const chatHistory = chatHistoryRef.current;
        if (chatHistory) {
            chatHistory.addEventListener('scroll', checkScrollPosition);
            return () => chatHistory.removeEventListener('scroll', checkScrollPosition);
        }
    }, [checkScrollPosition]);

    // Check scroll position when messages change
    useEffect(() => {
        checkScrollPosition();
    }, [messages, checkScrollPosition]);

    // Scroll to bottom handler
    const handleScrollToBottom = useCallback(() => {
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
    }, []);

    const handleFileSelection = useCallback((files: any) => {
        setIsFileUploadModalOpen(false)
        setCurrentFiles(files)
    }, []);

    const handleRemoveFile = useCallback((index: number) => {
        setCurrentFiles((files: any[]) => files.filter((_, i) => i !== index));
    }, []);

    const handleSendMessage = useCallback(async (message: string) => {
        // Clear input immediately
        setInputValue('');

        // Add user message
        const userMessage: ChatMessage = {
            id: Date.now(),
            role: 'user',
            content: message,
            file: null,
            preview: null,
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);

        // Add loading message
        const loadingMessage: ChatMessage = {
            id: Date.now() + 1,
            role: 'loading',
            content: '',
            preview: null
        };
        setMessages((prevMessages) => [...prevMessages, loadingMessage]);
        setLoading(true);

        commonService.handleMixpanelTrack("enhanced_requirement_gathering_send_message", {
            project_id: projectId,
            message: message,
            agent: selectedAgent,
            workflow_initialized: workflowInitialized,
            mode: 'text',
            is_requirement_gathering: true,
            timestamp: new Date().toISOString()
        });

        // Execute requirement gathering
        await executeRequirementGathering({
            projectId: projectId || '',
            message: message,
            previewUrl: iframeUrl || '',
            agentType: selectedAgent!,
            workflowInitialized: workflowInitialized,
            sessionId: sessionId || undefined,
            waitForPreviewUrl,
            onMessage: (responseMessage: any, isStreaming?: boolean) => {
                if (isStreaming) {
                    setMessages((prevMessages) => {
                        const lastMessage = prevMessages[prevMessages.length - 1];
                        const aiMessage: ChatMessage = {
                            id: responseMessage.id || Date.now(),
                            role: 'ai',
                            content: responseMessage.message,
                            file: null,
                            preview: null
                        };

                        if (lastMessage && lastMessage.role === 'ai') {
                            // Update existing AI message
                            return [...prevMessages.slice(0, -1), aiMessage];
                        } else if (lastMessage && lastMessage.role === 'loading') {
                            // Replace loading message with first AI chunk
                            return [...prevMessages.slice(0, -1), aiMessage];
                        }
                        return [...prevMessages, aiMessage];
                    });
                } else {
                    setMessages((prevMessages) => {
                        const filteredMessages = prevMessages.filter(msg => msg.role !== 'loading');
                        const lastIsAi = filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === 'ai';

                        const aiMessage: ChatMessage = {
                            id: responseMessage.id || Date.now(),
                            role: 'ai',
                            content: responseMessage.message,
                            file: null,
                            preview: null
                        };

                        if (lastIsAi) {
                            return [...filteredMessages.slice(0, -1), aiMessage];
                        }
                        return [...filteredMessages, aiMessage];
                    });
                    setLoading(false);
                }

                // Track session ID - only update if changed to avoid unnecessary re-renders
                if (responseMessage.extra?.session_id && responseMessage.extra.session_id !== sessionId) {
                    setSessionId(responseMessage.extra.session_id);
                }
            },
            onError: (errorMessage?: string) => {
                setMessages((prevMessages) => {
                    const filteredMessages = prevMessages.filter(msg => msg.role !== 'loading');
                    const errorMsg: ChatMessage = {
                        id: Date.now(),
                        role: 'ai',
                        content: errorMessage || 'Failed to send message. Please try again.',
                        file: null,
                        preview: null
                    };
                    return [...filteredMessages, errorMsg];
                });
                setLoading(false);
                commonService._alert.showToast(errorMessage || 'Failed to send message', 'error');
            },
            onComplete: () => {
                setLoading(false);
                dispatch(getUsersTokensUtilization());
            },
            onContainerWakeStart: () => {
                commonService._alert.showToast('Waking up container...', 'info');
            },
            onContainerWakeSuccess: () => {
                commonService._alert.showToast('Container ready!', 'success');
            }
        });
    }, [projectId, selectedAgent, workflowInitialized, executeRequirementGathering, iframeUrl, sessionId, waitForPreviewUrl, dispatch]);

    // Fetch project details on mount
    const fetchProjectDetails = useCallback((payload: any) => {
        commonService._project_service.getProjectDetails(payload, {})
            .then((response: any) => {
                dispatch(setCurrentProjectDetails(response?.data?.project));
                dispatch(setIframeUrl(response?.data?.project?.preview_url));
            })
            .catch((error: any) => {
                console.error('Error fetching project details:', error);
                commonService._alert.showToast('Error fetching project details', 'error');
            });
    }, [dispatch]);

    // Fetch project details when component mounts
    useEffect(() => {
        if (projectId) {
            fetchProjectDetails(projectId);
        }
    }, [projectId, fetchProjectDetails]);

    // Load state from IndexedDB on mount
    useEffect(() => {
        const loadStateFromDB = async () => {
            if (!projectId) {
                setIsLoadingFromDB(false);
                return;
            }

            try {
                const savedState = await requirementGatheringDB.loadState(projectId);
                if (savedState) {
                    // Restore state
                    setMessages(savedState.messages || []);

                    // If workflow was already initialized, skip initialization step and go to agent selection
                    if (savedState.workflowInitialized && savedState.currentStep === 'workflow-init') {
                        setCurrentStep('agent-selection');
                    } else {
                        setCurrentStep(savedState.currentStep as SetupStep);
                    }

                    setSelectedAgent(savedState.selectedAgent as AgentType | null);
                    setWorkflowInitialized(savedState.workflowInitialized);
                    setSessionId(savedState.sessionId);

                    commonService._alert.showToast("Conversation restored", "info");
                }
            } catch (error) {
                console.error('Error loading from IndexedDB:', error);
            } finally {
                setIsLoadingFromDB(false);
            }
        };

        loadStateFromDB();
    }, [projectId]);

    // Save state to IndexedDB whenever it changes
    useEffect(() => {
        const saveStateeToDB = async () => {
            if (!projectId || isLoadingFromDB) return;

            try {
                const state: RequirementGatheringState = {
                    projectId,
                    messages,
                    currentStep,
                    selectedAgent,
                    selectedMode: null,
                    workflowInitialized,
                    sessionId,
                    lastUpdated: Date.now()
                };

                await requirementGatheringDB.saveState(state);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        };

        saveStateeToDB();
    }, [projectId, messages, currentStep, selectedAgent, workflowInitialized, sessionId, isLoadingFromDB]);




    // Handle workflow initialization choice
    const handleWorkflowInitChoice = useCallback(async (shouldInitialize: boolean) => {
        setWorkflowInitialized(shouldInitialize);

        commonService.handleMixpanelTrack("Workflow Initialization Choice", {
            project_id: projectId,
            workflow_initialized: shouldInitialize,
            is_requirement_gathering: true,
            timestamp: new Date().toISOString()
        });

        // If workflow is initialized, run package installer first
        if (shouldInitialize) {
            setCurrentStep('initializing');
            commonService._alert.showToast('Installing SuperEngineer agents...', 'info');

            // Execute package installer invisibly
            await executeRequirementGathering({
                projectId: projectId || '',
                message: PACKAGE_INSTALLER_COMMAND,
                previewUrl: iframeUrl || '',
                agentType: 'analyst', // Temporary agent type for installation
                workflowInitialized: true,
                sessionId: sessionId || undefined,
                waitForPreviewUrl,
                onMessage: (message: any) => {
                    // Track session ID but don't show installation messages
                    if (message.extra?.session_id && message.extra.session_id !== sessionId) {
                        setSessionId(message.extra.session_id);
                    }
                },
                onError: (errorMessage?: string) => {
                    commonService._alert.showToast(errorMessage || 'Failed to install agents', 'error');
                    // Continue to agent selection anyway
                    setCurrentStep('agent-selection');
                },
                onComplete: () => {
                    // Installation complete, proceed to agent selection
                    commonService._alert.showToast('Agents installed successfully!', 'success');
                    setCurrentStep('agent-selection');
                },
                onContainerWakeStart: () => {
                    commonService._alert.showToast('Starting container...', 'info');
                },
                onContainerWakeSuccess: () => {
                    commonService._alert.showToast('Container ready!', 'success');
                }
            });
        } else {
            // Skip installation and specialized scoping, go back to main screen
            navigate(PROJECT_REACT_ROUTE + '/' + projectId);
        }
    }, [projectId, executeRequirementGathering, iframeUrl, sessionId, waitForPreviewUrl, navigate]);

    // Handle agent selection
    const handleAgentSelection = useCallback(async (agent: AgentType) => {
        setSelectedAgent(agent);

        const agentName = agent === 'analyst' ? 'Business Analyst' : 'Product Manager';
        commonService.handleMixpanelTrack("Agent Selected", {
            project_id: projectId,
            agent: agentName,
            workflow_initialized: workflowInitialized,
            is_requirement_gathering: true,
            timestamp: new Date().toISOString()
        });

        // If workflow was initialized, run agent-specific command
        if (workflowInitialized) {
            setCurrentStep('initializing');
            const initCommand = AGENT_INITIALIZATION_COMMANDS[agent];
            commonService._alert.showToast(`Initializing ${agentName}...`, 'info');

            // Execute agent-specific initialization invisibly
            await executeRequirementGathering({
                projectId: projectId || '',
                message: initCommand,
                previewUrl: iframeUrl || '',
                agentType: agent,
                workflowInitialized: true,
                sessionId: sessionId || undefined,
                waitForPreviewUrl,
                onMessage: (message: any) => {
                    // Track session ID but don't show initialization messages
                    if (message.extra?.session_id && message.extra.session_id !== sessionId) {
                        setSessionId(message.extra.session_id);
                    }
                },
                onError: (errorMessage?: string) => {
                    commonService._alert.showToast(errorMessage || 'Failed to initialize agent', 'error');
                    // Continue to conversation anyway
                    setCurrentStep('conversation');
                },
                onComplete: () => {
                    // Agent initialization complete, start conversation
                    commonService._alert.showToast(`${agentName} initialized!`, 'success');
                    setCurrentStep('conversation');
                },
                onContainerWakeStart: () => {
                    commonService._alert.showToast('Waking up container...', 'info');
                },
                onContainerWakeSuccess: () => {
                    commonService._alert.showToast('Container ready!', 'success');
                }
            });
        } else {
            // Skip agent initialization, go directly to conversation
            setCurrentStep('conversation');
        }
    }, [projectId, workflowInitialized, executeRequirementGathering, iframeUrl, sessionId, waitForPreviewUrl]);

    // Send initial greeting when conversation starts
    useEffect(() => {
        if (currentStep === 'conversation' && messages.length === 0 && selectedAgent) {
            const agentName = selectedAgent === 'analyst' ? 'Business Analyst' : 'Product Manager';
            const initialMessage = workflowInitialized
                ? `Hello! I'm your ${agentName}. I'm ready to help you gather requirements for your project with a structured workflow approach.`
                : `Hello! I'm your ${agentName}. I'm ready to help you gather requirements for your project.`;

            // Add loading message
            const loadingMessage: ChatMessage = {
                id: Date.now(),
                role: 'loading',
                content: '',
                file: null,
                preview: null
            };
            setMessages([loadingMessage]);
            setLoading(true);

            // Send greeting
            executeRequirementGathering({
                projectId: projectId || '',
                message: initialMessage,
                previewUrl: iframeUrl || '',
                agentType: selectedAgent,
                workflowInitialized: workflowInitialized,
                sessionId: sessionId || undefined,
                waitForPreviewUrl,
                onMessage: (message: any, isStreaming?: boolean) => {
                    if (!isStreaming) {
                        setMessages((prevMessages) => {
                            const filtered = prevMessages.filter(msg => msg.role !== 'loading');
                            return [...filtered, {
                                id: message.id || Date.now(),
                                role: 'ai',
                                content: message.message,
                                file: null,
                                preview: null
                            }];
                        });
                        setLoading(false);
                    }
                    if (message.extra?.session_id && message.extra.session_id !== sessionId) {
                        setSessionId(message.extra.session_id);
                    }
                },
                onError: (errorMessage?: string) => {
                    setMessages([{
                        id: Date.now(),
                        role: 'ai',
                        content: errorMessage || 'Failed to start. Please try again.',
                        file: null,
                        preview: null
                    }]);
                    setLoading(false);
                },
                onComplete: () => {
                    setLoading(false);
                    dispatch(getUsersTokensUtilization());
                },
                onContainerWakeStart: () => {
                    commonService._alert.showToast('Starting container...', 'info');
                },
                onContainerWakeSuccess: () => {
                    commonService._alert.showToast('Container ready!', 'success');
                }
            });
        }
    }, [currentStep, messages.length, selectedAgent, workflowInitialized, projectId, iframeUrl, sessionId, waitForPreviewUrl, executeRequirementGathering, dispatch]);



    // Handle starting new conversation - show workflow init screen again
    const handleStartNewConversation = useCallback(async () => {

        // Clear IndexedDB state
        if (projectId) {
            try {
                await requirementGatheringDB.clearState(projectId);
            } catch (error) {
                console.error('Error clearing IndexedDB:', error);
            }
        }

        // Reset all local component state to initial
        setMessages([]);
        setInputValue('');
        setLoading(false);
        setCurrentFiles(null);
        setImageFile(null);
        setIsFileUploadModalOpen(false);

        // Reset setup flow
        setCurrentStep('workflow-init');
        setWorkflowInitialized(false);
        setSelectedAgent(null);

        commonService._alert.showToast("Conversation reset. Please go through setup again.", "info");
    }, [projectId]);

    // Determine if we're in setup phase
    const isInSetup = currentStep !== 'conversation';

    return (
        <>
            <div className={`flex-1 flex flex-col items-center enhanced-requirement-gathering-container ${isMobile ? 'requirement-gathering-mobile' : ''}`}>
                {/* Top Action Buttons - Skip and Restart */}
                <div className="w-full max-w-2xl flex justify-between gap-2">
                    {/* Restart button - show when conversation has started */}
                    {currentStep === 'conversation' && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleStartNewConversation}
                                        className="text-sm gap-2"
                                        mixpanelEvent={{
                                            name: "Restart Enhanced Conversation",
                                            properties: {
                                                project_id: projectId,
                                                agent: selectedAgent,
                                                workflow_initialized: workflowInitialized,
                                                is_requirement_gathering: true
                                            }
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                            <path d="M21 3v5h-5" />
                                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                            <path d="M3 21v-5h5" />
                                        </svg>
                                        <span className="hidden sm:inline">Restart</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Restart conversation and go through setup again</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}

                    {/* Spacer when restart button is not visible */}
                    {currentStep !== 'conversation' && <div />}

                    {/* Skip button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            navigate(PROJECT_REACT_ROUTE + '/' + projectId);
                            commonService._alert.showToast("Skipped project scoping", "info");
                        }}
                        className="text-sm text-muted-foreground hover:text-foreground"
                        mixpanelEvent={{
                            name: "Skip Enhanced Project Scoping",
                            properties: {
                                project_id: projectId,
                                is_requirement_gathering: true
                            }
                        }}
                    >
                        Skip project scoping
                    </Button>
                </div>
                {
                    isNew && isInSetup && (
                        <div className={'welcome-container'}>
                            {/* Loading during initialization */}
                            {currentStep === 'initializing' && (
                                <div className="flex flex-col items-center justify-center gap-4 py-12">
                                    <ChatLoaderComponent type="requirement-step" />
                                    <p className="text-sm text-muted-foreground">
                                        {!selectedAgent ? 'Installing SuperEngineer agents...' : `Initializing ${selectedAgent === 'analyst' ? 'Business Analyst' : 'Product Manager'}...`}
                                    </p>
                                </div>
                            )}

                            {/* Welcome Message - Show only on first step */}
                            {currentStep === 'workflow-init' && (
                                <RequirementGatheringWelcomeComponent
                                    greeting={greeting}
                                    userName={userName}
                                />
                            )}

                            {/* Workflow Initialization Selection */}
                            {currentStep === 'workflow-init' && (
                                <WorkflowInitializationComponent onInitializationChoice={handleWorkflowInitChoice} />
                            )}

                            {/* Agent Selection */}
                            {currentStep === 'agent-selection' && (
                                <AgentSelectionComponent onAgentSelection={handleAgentSelection} />
                            )}


                        </div>
                    )
                }
                <div className={"max-w-2xl w-full " + ((!isNew || !isInSetup) && " requirement-chat-container")} style={{ position: 'relative' }}>
                    <div className={'chat-history'} ref={chatHistoryRef}>
                        {
                            messages?.map((message) => {
                                return (
                                    <div className={`message ${message.role}`} key={message.id + message.role}>
                                        {
                                            (message.role === 'ai' || message.role === 'loading') ?
                                                <JoyAvatar size={'chat'} /> :
                                                <UserAvatarComponent />
                                        }
                                        <motion.div className="content"
                                            initial={{ y: 100 }}
                                            animate={{ y: 0 }}
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {renderMessages(message) as ReactNode}
                                        </motion.div>
                                    </div>
                                )
                            })
                        }
                    </div>

                    {showScrollIndicator && (
                        <div style={{
                            position: 'absolute',
                            bottom: isMobile ? '150px' : '160px',
                            right: isMobile ? '10px' : '20px',
                            zIndex: 10,
                        }}>
                            <Button
                                onClick={handleScrollToBottom}
                                style={{
                                    borderRadius: '50%',
                                    justifyContent: 'center',
                                    width: '40px',
                                    height: '40px',
                                }}
                                variant="secondaryForeground"
                                mixpanelEvent={{
                                    name: "requirement_gathering_scroll_to_bottom",
                                    properties: {
                                        project_id: projectId,
                                        is_requirement_gathering: true
                                    }
                                }}
                            >
                                <MoveDownIcon size={16} color="white" />
                            </Button>
                        </div>
                    )}

                    <>


                        {/* Control Panel - Show after setup */}
                        {currentStep === 'conversation' && (
                            <div className="w-full max-w-2xl mb-4">
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    {/* Agent Indicator */}
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                        {selectedAgent === 'analyst' ? 'Business Analyst' : 'Product Manager'}
                                    </div>



                                    {/* Go to App Building Button - Show when there are multiple messages */}
                                    {messages.length > 1 && (
                                        <Button
                                            onClick={() => navigate(PROJECT_REACT_ROUTE + '/' + projectId)}
                                            variant="default"
                                            size="sm"
                                            className="gap-2"
                                            mixpanelEvent={{
                                                name: "Go to App Building from Requirements",
                                                properties: {
                                                    project_id: projectId,
                                                    agent: selectedAgent,
                                                    workflow_initialized: workflowInitialized,
                                                    is_requirement_gathering: true
                                                }
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14" />
                                                <path d="m12 5 7 7-7 7" />
                                            </svg>
                                            <span className="hidden sm:inline">Go to App Building</span>
                                            <span className="sm:hidden">Build App</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}


                        <div className={isMobile ? 'custom-input-mobile' : ''}>
                            {currentStep === 'conversation' && (
                                <CustomInputComponent
                                    onSend={(message: string) => {
                                        handleSendMessage(message);
                                    }}
                                    selectedFiles={currentFiles}
                                    projectId={projectId}
                                    loading={loading}
                                    isNew={messages?.length === 0}
                                    onFileRemove={(index: number) => handleRemoveFile(index)}
                                    value={inputValue}
                                    onInputChange={(value: string) => {
                                        setInputValue(value);
                                    }}
                                    imageFile={imageFile}
                                    onImageFileChange={setImageFile}


                                    isRequirementGathering={true}
                                    hideFileAttachment={true}
                                    hideEnhancement={true}
                                />
                            )}
                        </div>
                    </>

                </div>


                <Dialog open={isFileUploadModalOpen} onOpenChange={() => setIsFileUploadModalOpen(false)}>
                    <DialogContent className={`ps-0 ${isMobile ? 'file-upload-dialog-mobile' : ''}`}>
                        <FileUploadComponent onUpload={handleFileSelection} isMultiple={false} />
                    </DialogContent>
                </Dialog>

            </div>


        </>
    );
};

export default EnhancedRequirementGatheringComponent;
